import React from "react";
import db from "../../../services/query";
import AriaReport, {
  chunkDays,
  faDigits,
  ReportHeader,
  ReportSignatures,
  sheetDensity,
  shortShamsi,
  usePrintData,
  weekdayFa,
  buildPrintDocumentTitle,
} from "./AriaReport";

const TITLES = {
  0: "جدول حاضری دانشجویان (تمام سمستر)",
  1: "جدول حاضری دانشجویان (نیمه اول سمستر)",
  2: "جدول حاضری دانشجویان (نیمه دوم سمستر)",
};

export default function SubjectAttendancePrint({ match, location }) {
  const { classId, subjectId, type } = match.params;
  const lectureId = new URLSearchParams(location.search).get("lectureId") || "";
  const { loading, error, data, summary } = usePrintData(
    async () => {
      const params = new URLSearchParams({ classId, subjectId, type: type || "0" });
      if (lectureId) params.set("lectureId", lectureId);
      const { data: body } = await db.getAll(`mis-parity/reports/subject-attendance?${params}`);
      return body;
    },
    [classId, subjectId, type, lectureId]
  );
  const students = Array.isArray(data) ? data : [];
  const days = summary?.days || [];
  const header = summary?.header || days[0] || {};
  const cls = summary?.cls || {};
  const sheets = chunkDays(days, 32);
  const title = TITLES[type] || TITLES[0];
  const credits = header.credits ?? days.find((day) => day.credits != null)?.credits;
  const faculty = header.faculty || cls.faculty_name;
  const classDesc = header.class_desc || cls.class_desc || "";
  const yearLabel = faDigits(header.entry_year_fa || cls.entry_year_fa || "");
  const classLabel = yearLabel ? `${classDesc} (${yearLabel})`.trim() : classDesc;
  const rightLines = [
    title,
    header.subject_name ? `${header.subject_name} (${faDigits(credits || 0)} کریدت)` : "",
    header.teacher_name,
    classLabel,
  ];
  const leftLines = ["وزارت تحصیلات عالی", "پوهنتون آریا", faculty, "مدیریت تدریسی"];

  if (loading) return <p className="aria-report-status">در حال آماده‌سازی گزارش…</p>;
  if (error) return <p className="aria-report-status" role="alert">{error}</p>;

  let fromDay = 1;
  return (
    <AriaReport
      className={`subject-attendance-report print-attendance-density-${sheetDensity(Math.max(0, ...sheets.map((sheet) => sheet.length)), type)}${String(type) === "0" ? " is-full-semester" : ""}`}
      landscape
      pageMargin="5mm"
      showHeader={false}
      documentTitle={buildPrintDocumentTitle([
        "حاضری مضمون",
        header.subject_name,
        yearLabel,
      ])}
    >
      {sheets.map((sheetDays, sheetIndex) => {
        const density = sheetDensity(sheetDays.length, type);
        const toDay = fromDay + Math.max(0, sheetDays.length) - 1;
        const start = fromDay;
        fromDay += Math.max(0, sheetDays.length);
        return (
          <section
            className={`print-sheet print-attendance-density-${density}`}
            key={`sheet-${sheetIndex}`}
          >
            <ReportHeader rightLines={rightLines} leftLines={leftLines} />
            <div className="sheet-meta">
              <span>
                {sheetDays.length
                  ? `ستون‌های تاریخ: ${faDigits(start)} تا ${faDigits(toDay)} از ${faDigits(days.length)}`
                  : "تاریخی برای نمایش وجود ندارد"}
              </span>
              <span>
                صفحه {faDigits(sheetIndex + 1)} از {faDigits(sheets.length)}
              </span>
            </div>
            <table className="aria-report-table attendance-grid">
              <thead>
                <tr>
                  <th className="col-num">شماره</th>
                  <th className="col-name">نام</th>
                  <th className="col-fname">نام پدر</th>
                  <th className="col-code">کد دانشجویی</th>
                  {sheetDays.map((day, dayIndex) => (
                    <th
                      className="day-col"
                      key={`${day.lecture_id || dayIndex}-${day.lecture_date || day.shamsi_date}`}
                      title={day.shamsi_date || day.lecture_date || ""}
                    >
                      <span className="vlabel">
                        <span className="wd">{weekdayFa(day.weekday_name_fa)}</span>
                        <span className="dt">{faDigits(shortShamsi(day.shamsi_date || day.lecture_date))}</span>
                      </span>
                    </th>
                  ))}
                  <th className="col-sum">غیر حاضر</th>
                  <th className="col-sum">حاضر</th>
                  <th className="col-notes">ملاحظات</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan={7 + sheetDays.length}>
                      <div className="aria-report-empty">محصلی برای این صنف ثبت نشده است</div>
                    </td>
                  </tr>
                ) : (
                  students.map((student, index) => (
                    <tr key={student.student_id || index}>
                      <td className="col-num">{faDigits(index + 1)}</td>
                      <td className="col-name">{student.student_name}</td>
                      <td className="col-fname">{student.student_father_name}</td>
                      <td className="col-code">{student.aria_id}</td>
                      {sheetDays.map((day, dayIndex) => (
                        <td className="day-col" key={`${student.student_id}-${day.lecture_id || dayIndex}`} />
                      ))}
                      <td className="col-sum" />
                      <td className="col-sum" />
                      <td className="col-notes" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {sheetIndex === sheets.length - 1 ? <ReportSignatures labels={["استاد"]} /> : null}
          </section>
        );
      })}
    </AriaReport>
  );
}
